import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { size as _size, includes, isNil, noop, toNumber, toString } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';
import queryString from '@tock/query-string';

import {
  ACTION_EVENT_VIEW_PAGE,
  ACTION_SEARCH_EXPERIENCE_CALENDAR,
  ACTION_VIEW_PAGE,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { AmplitudeSendEventProps, withAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { AmplitudeSendOnce, experienceToData } from 'utils/analytics/amplitude';
import { Dialog, DialogContent, DialogHeading } from 'omakase';
import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { LockOptions } from 'types';
import { adjustDateForCutOff } from 'utils/constants';
import {
  calendarSelector,
  experienceAvailableSizesSelector,
  experienceSelector,
  offeringsSelector,
} from 'selectors/calendarSelectors';
import { joinPath } from 'utils/pathUtils';
import { offeringSlugConsumerRoutePattern } from '@tock/shared/utils/offerings';
import { resolveSchedule } from '@tock/time';
import { tryLockAndContinue } from 'utils/lock';
import BaseComponent from '@tock/shared/components/BaseComponent';
import BusinessProfileContent from 'components/business/landing-page/BusinessProfileContent';
import ExperienceNotFound from 'components/business/search/ExperienceNotFound';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import OfferingDetailContent from 'components/offering-detail-page/OfferingDetailContent';
import SearchModalRoute from 'routes/business/SearchModalRoute';
import WaitlistRoute, { WaitlistLocationState } from 'routes/business/WaitlistRoute';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

type ExperienceDetail = StoreType['calendar']['experienceDetail'];
interface ConnectProps extends DefaultConnectProps, ExperienceDetail {
  experience?: core.ITicketType;
  lock: StoreType['lock'];
  availablePurchaseSizes: number[];
  offerings?: consumer.ConsumerOfferings;
  calendar: StoreType['calendar'];
  sessionToken?: string;
  isInstrumentationEnabled?: boolean;
}

interface MatchParams {
  businessDomain: string;
  experienceId?: string;
  ticketTypeAccessToken?: string;
}

type NonConnectProps = RouteComponentProps<MatchParams>;

type Props = ConnectProps & NonConnectProps & IWithContextProps & AmplitudeSendEventProps;

class OfferingDetailRoute extends BaseComponent<Props> {
  eventSent = false;

  componentDidMount() {
    this.fetchOfferingIfNeeded();
  }

  /**
   * If we loose the offering make sure to try and re-fetch it
   * @see https://tock.atlassian.net/browse/TK-8743
   */
  componentDidUpdate() {
    this.fetchOfferingIfNeeded();
    // Send an event once when the page is viewed
    if (!this.eventSent && this.props.isInstrumentationEnabled && this.props.sessionToken) {
      this.props.amplitudeSendEvent(ACTION_EVENT_VIEW_PAGE, {
        businessId: this.props.app?.config?.business?.id ?? undefined,
        sessionToken: this.props.sessionToken,
      });
      this.eventSent = true;
    }
  }

  fetchOfferingIfNeeded() {
    const {
      match: {
        params: { experienceId, ticketTypeAccessToken },
      },
      requestFailed,
      requestInProgress,
      offering,
    } = this.props;

    if (requestInProgress || requestFailed) {
      return;
    }

    if (isNil(offering)) {
      this.fetchOffering(experienceId, ticketTypeAccessToken);
    } else {
      const offeringIsStale = !isNil(experienceId) && parseInt(experienceId, 10) !== offering.id;
      if (offeringIsStale) {
        this.fetchOffering(experienceId);
      }
    }
  }

  fetchOffering(experienceId?: string, ticketTypeAccessToken?: string): void {
    const { actions, location } = this.props;
    const { previewMode, endDate } = queryString.parse(location.search);
    if (experienceId) {
      if (previewMode) {
        actions.calendar.fetchSingleOfferingPreview(parseInt(experienceId, 10), undefined, endDate);
      } else {
        actions.calendar.fetchSingleOffering(parseInt(experienceId, 10));
      }
    } else if (ticketTypeAccessToken) {
      if (previewMode) {
        actions.calendar.fetchSingleOfferingPreview(undefined, ticketTypeAccessToken, endDate);
      } else {
        actions.calendar.fetchSingleOffering(undefined, ticketTypeAccessToken);
      }
    }
  }

  handleCollectButtonClickData() {
    const { app, offering } = this.props;
    const business = app.config?.business;
    if (!offering || !business) {
      return;
    }
    window.analytics.sendEvent(
      'Offering detail page',
      'Get tickets button click',
      `${business.name} - ${offering.id}`
    );
  }

  handleLockTickets(size: number) {
    if (!this.props.offering) {
      return;
    }
    const { id, eventDetails } = this.props.offering;
    const { schedule, priceCents, startTime } = eventDetails as consumer.EventDetails;
    const dates = resolveSchedule(schedule[0]);
    if (_size(dates) !== 1) {
      return this.handleSearch(size);
    }

    const { app, lock } = this.props;
    const dateTimeToLock = adjustDateForCutOff(toString(dates[0]), startTime);
    const options: LockOptions = {
      showError: true,
    };
    const business = app.config?.business;
    return tryLockAndContinue(
      this.props.actions,
      id,
      size,
      lock,
      dateTimeToLock,
      priceCents,
      options,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      business!
    )
      .then(() => {
        this.handleCollectButtonClickData();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.props.history.push(`/${business!.domainName}/checkout/edit-guest-profile`);
      })
      .catch(noop);
  }

  handleSearch(partySize: number, selectedDate?: string, selectedTime?: string) {
    const {
      match: { url },
      location: { search },
      experience,
      amplitudeSendEvent,
    } = this.props;
    amplitudeSendEvent(ACTION_SEARCH_EXPERIENCE_CALENDAR, {
      ...experienceToData(experience),
      partySize: partySize,
    });
    this.props.history.push({
      pathname: joinPath(url, `/book`),
      search: queryString.stringify({
        ...queryString.parse(search),
        size: partySize,
      }),
    });
  }

  handleOpenWaitlist() {
    const {
      match: { url },
      location: { search },
    } = this.props;
    this.handleCollectButtonClickData();
    this.props.history.push({
      pathname: joinPath(url, `/waitlist`),
      search: queryString.stringify({
        ...queryString.parse(search),
        size: 1,
        fromPage: 'event_detail',
      }),
    });
  }

  handleClose() {
    const {
      match: {
        params: { businessDomain },
      },
    } = this.props;
    this.props.history.push(`/${businessDomain}`);
  }

  renderSearchModal(routeProps: RouteComponentProps<MatchParams>) {
    return <SearchModalRoute {...routeProps} backUrl={this.props.match.url} />;
  }

  renderWaitlistModal(routeProps: RouteComponentProps<MatchParams, {}, WaitlistLocationState>) {
    const experienceId = oc(this.props.experience).id() || undefined;
    return (
      <WaitlistRoute {...routeProps} experienceId={experienceId} backUrl={this.props.match.url} />
    );
  }

  render() {
    const {
      app,
      availablePurchaseSizes,
      experience,
      location,
      offering,
      offerings,
      match: { params },
      requestFailed,
      calendar,
    } = this.props;
    const business = app?.config?.business;

    if (!business) {
      return null;
    }

    const isPrivate = includes(offerings?.privateExperienceId, toNumber(params.experienceId));
    const notEnabled = !isNil(offering) && !offering.hasDetailPage;
    if (requestFailed || notEnabled) {
      return (
        <>
          <BusinessProfileContent
            business={business as consumer.BusinessConfig}
            isLoading={false}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            nextRelease={'' as any}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            offerings={offerings!}
            onAddTicketTypeToWaitlist={noop}
            onMobileSearch={noop}
            onSearch={this.handleSearch}
            ticketGroups={[]}
          />
          <Dialog open={true} onClose={this.handleClose}>
            <DialogHeading onClose={this.handleClose}>
              {isPrivate ? `Event is access listed` : `Event not found`}
            </DialogHeading>
            <DialogContent disablePadding>
              <ExperienceNotFound
                businessEmail={business.email}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                businessName={business.name!}
                isPrivateExperience={isPrivate}
                isWidget={false}
                location={location}
                isAuthenticated={!!app.jwtToken}
                isOfferingEvent={true}
                onButtonClick={this.handleClose}
              />
            </DialogContent>
          </Dialog>
        </>
      );
    }

    return (
      <>
        <Switch>
          <Route
            path={[
              `/:businessDomain/${offeringSlugConsumerRoutePattern('offering')}/:experienceId/book`,
              `/:businessDomain/${offeringSlugConsumerRoutePattern(
                'offering'
              )}/private/:ticketTypeAccessToken/book`,
            ]}
            render={this.renderSearchModal}
          />
          <Route
            path={[
              `/:businessDomain/${offeringSlugConsumerRoutePattern(
                'offering'
              )}/:experienceId/waitlist`,
              `/:businessDomain/${offeringSlugConsumerRoutePattern(
                'offering'
              )}/private/:ticketTypeAccessToken/waitlist`,
            ]}
          >
            {(routeProps: RouteComponentProps<MatchParams, {}, WaitlistLocationState>) =>
              this.renderWaitlistModal(routeProps)
            }
          </Route>
        </Switch>
        <LoadingComponent
          containerClassName="MainFooter--fixed-footer-sibling"
          isLoading={isNil(offering)}
        >
          {experience && (
            <AmplitudeSendOnce
              event={ACTION_VIEW_PAGE}
              data={{
                pageType: 'Offering detail',
                ...experienceToData(experience),
              }}
            />
          )}
          <OfferingDetailContent
            availablePurchaseSizes={availablePurchaseSizes}
            experience={experience}
            business={business as consumer.BusinessConfig}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            offering={offering!}
            ticketGroups={calendar.calendar?.ticketGroup as consumer.CalendarTicketGroup[]}
            onSelectTickets={this.handleLockTickets}
            onSearch={this.handleSearch}
            onOpenWaitlist={this.handleOpenWaitlist}
          />
        </LoadingComponent>
      </>
    );
  }
}

export default connect<NonConnectProps, ConnectProps>(
  withAmplitudeSendEvent(withContext(OfferingDetailRoute)),
  (state: StoreType, props?: NonConnectProps) => {
    const idFromPath = toNumber(props?.match.params.experienceId);

    const { experienceDetail } = state.calendar;
    const experienceFromCalendar = experienceSelector(state, idFromPath);

    const availablePurchaseSizes = isNil(experienceFromCalendar)
      ? experienceDetail.offering?.purchaseSize || []
      : experienceAvailableSizesSelector(state, idFromPath);

    return {
      offerings: offeringsSelector(state),
      experience: experienceFromCalendar,
      ...state.calendar.experienceDetail,
      availablePurchaseSizes,
      lock: state.lock,
      calendar: calendarSelector(state),
      sessionToken: state.app.sessionToken,
      isInstrumentationEnabled:
        state.features.features['CONSUMER_VENUE_PAGE_CONVERSION_INSTRUMENTATION'] || false,
    };
  }
);
